import React, { useCallback, useMemo, useState, useRef, useEffect } from "react";
import DoctorHeader from "./DoctorHeader";
import DoctorSidebar, { DoctorSidebarDrawer } from "./DoctorSidebar";
import {
  IconButton,
  CircularProgress,
  Popover,
  SwipeableDrawer,
} from "@mui/material";
import {
  Menu,
  Send,
  Info,
  Smile,
  FileText,
  ArrowLeft,
  CreditCard,
  Reply,
  X,
  Phone,
  MessageCircle,
  Pill,
} from "lucide-react";
import EmojiPicker from "emoji-picker-react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { io } from "socket.io-client";
import ChatBubble from "./ChatBubble";
import PatientInfoPanel from "./PatientInfoPanel";
import SimpleSnackbar from "../Snackbar";
import hospitalsList from "../../hospitals";
import StartCallButton from "../../utils/StartCallButton";
import PrescriptionsDialog from "./PrescriptionsDialog";
import SpecialistsDialog from "./ShowSpecialistModal";
import ReferralDialog from "./ReferralModal";
import ZavLoader from "../ZavLoader";
import { updatePatientInCache } from "../../cache/patientsCache";
import { notifyDoctorUnreadMessagesChanged } from "../../hooks/useDoctorUnreadMessages";

const socket = io(process.env.REACT_APP_BACKEND_URL, {
  reconnection: true,
  reconnectionAttempts: 5,
  reconnectionDelay: 3000,
  auth: {
    token: localStorage.getItem("token"),
  },
});

const getMessageId = (message) =>
  message?._id?.toString?.() ||
  message?.id?.toString?.() ||
  (message?.timestamp ? String(message.timestamp) : "");

const cleanReplyPreview = (message) => {
  const text = message?.text || message?.message || "";

  if (text.trim()) {
    const cleanText = text.replace(/\s+/g, " ").trim();
    return cleanText.length > 160 ? `${cleanText.slice(0, 157)}...` : cleanText;
  }

  const mediaType = message?.media?.[0]?.type || "";

  if (mediaType.includes("pdf")) return "PDF document";
  if (mediaType.startsWith("image")) return "Photo";
  if (mediaType.startsWith("video")) return "Video";
  if (mediaType.startsWith("audio")) return "Voice note";
  if (mediaType) return "Media message";

  return "Message";
};

const replyLabel = (sender, patientName) => {
  if (sender === "doctor") return "your message";
  if (sender === "client") return patientName || "patient";
  return "message";
};

const normalizeCostingStatus = (status = "") => {
  if (status === "pending") return "uncosted";
  if (status === "closed") return "delivered";
  return status || "uncosted";
};

const statusLabel = (status = "") => {
  const normalized = normalizeCostingStatus(status);
  if (normalized === "uncosted") return "Not costed";
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const formatMoney = (value) => {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount) || amount <= 0) return "";

  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatChatTime = (value) => {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  return date
    .toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
    .toLowerCase();
};

const getPharmacyContactNumber = (pharmacy = {}) =>
  String(pharmacy.whatsappNumber || pharmacy.phoneNumber || "").replace(
    /[^\d]/g,
    ""
  );

const PharmacyCostingChatCard = ({ request, onCall, onWhatsApp }) => {
  if (!request) return null;

  const status = normalizeCostingStatus(request.status);
  const isUncosted = status === "uncosted";
  const pharmacy = request.pharmacyId || {};
  const contactNumber = getPharmacyContactNumber(pharmacy);
  const total = formatMoney(request.totalCost);
  const drugs = Array.isArray(request.drugs) ? request.drugs : [];
  const initiatedTime = formatChatTime(request.createdAt);

  return (
    <div className="flex justify-end pb-3">
      <div className="max-w-[92%] rounded-2xl border border-emerald-900/10 bg-[#075E54] p-4 text-sm text-white shadow-sm sm:max-w-[72%]">
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/12 text-white">
              <Pill size={17} />
            </div>

            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-semibold text-white">
                  Pharmacy costing
                </p>
                <span className="rounded-full bg-white/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white/75">
                  Doctor only
                </span>
              </div>

              <p className="mt-1 text-sm leading-5 text-white/80">
                {pharmacy.name || "Selected pharmacy"}{" "}
                {isUncosted
                  ? "has not costed this medication yet."
                  : `status is ${statusLabel(status).toLowerCase()}.`}
              </p>
            </div>
          </div>

          <span
            className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${
              isUncosted
                ? "bg-amber-100 text-amber-800"
                : "bg-white text-[#075E54]"
            }`}
          >
            {statusLabel(status)}
          </span>
        </div>

        <div className="mt-3 rounded-xl bg-white/10 p-3">
          <p className="text-xs font-medium uppercase tracking-wide text-white/60">
            Medication
          </p>
          <div className="mt-2 space-y-1 text-sm text-white/90">
            {drugs.slice(0, 4).map((drug, index) => (
              <p key={`${drug.name || "drug"}-${index}`}>
                {drug.name || "Unnamed medicine"}
                {drug.quantity ? `, ${drug.quantity}` : ""}
                {drug.price ? ` - ${formatMoney(drug.price)}` : ""}
              </p>
            ))}
            {!drugs.length && <p>No medicines listed.</p>}
            {drugs.length > 4 && (
              <p className="text-xs text-white/60">
                +{drugs.length - 4} more medicine
                {drugs.length - 4 === 1 ? "" : "s"}
              </p>
            )}
          </div>
        </div>

        {!isUncosted && total && (
          <div className="mt-3 flex items-center justify-between rounded-xl border border-white/15 bg-white/10 px-3 py-2">
            <span className="text-sm font-medium text-white/80">
              Total cost
            </span>
            <span className="text-sm font-semibold text-white">
              {total}
            </span>
          </div>
        )}

        {isUncosted && (
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => onWhatsApp(request)}
              disabled={!contactNumber}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-white/20 bg-white px-3 text-sm font-semibold text-[#075E54] transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <MessageCircle size={15} />
              WhatsApp pharmacy
            </button>

            <button
              type="button"
              onClick={() => onCall(pharmacy)}
              disabled={!pharmacy.phoneNumber}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/10 px-3 text-sm font-semibold text-white transition hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Phone size={15} />
              Call pharmacy
            </button>
          </div>
        )}

        <p className="mt-3 text-right text-[11px] text-white/60">
          {initiatedTime ? `Initiated ${initiatedTime}. ` : ""}
          This card is visible to the doctor only.
        </p>
      </div>
    </div>
  );
};

const DoctorChatPage = () => {
  const navigate = useNavigate();
  const { client_id } = useParams();

  const doctor = JSON.parse(localStorage.getItem("doctor") || "{}");

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [infoDrawerOpen, setInfoDrawerOpen] = useState(false);

  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [replyTarget, setReplyTarget] = useState(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState("");
  const [patient, setPatient] = useState(null);
  const [activeTab, setActiveTab] = useState("Patients");

  const bottomRef = useRef(null);
  const messageRefs = useRef({});
  const textareaRef = useRef(null);
  const isUserAtBottom = useRef(true);

  const [openPrescriptions, setOpenPrescriptions] = useState(false);
  const [openSpecialistConsultation, setOpenSpecialistConsultation] =
    useState(false);

  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("success");

  const [sendingMessage, setSendMessage] = useState(false);
  const [sendingPaymentLink, setSendingPaymentLink] = useState(false);
  const [sendingPaymentLinkType, setSendingPaymentLinkType] = useState("");
  const [paymentLinkModalOpen, setPaymentLinkModalOpen] = useState(false);
  const [sub, setSub] = useState(null);
  const [costingRequests, setCostingRequests] = useState([]);

  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const [templateAnchor, setTemplateAnchor] = useState(null);

  const [showScrollButton, setShowScrollButton] = useState(false);

  const [referralOpen, setReferralOpen] = useState(false);
  const [selectedHospital, setSelectedHospital] = useState(null);
  const [hospitals, setHospitals] = useState(hospitalsList);

  const [hideAudioInput, setHideAudioInput] = useState(false);

  const quickTemplates = [
    "Hello 👋\n\nThank you for contacting the doctor.",
    "Please send a clear photo of the affected area.",
    "Please take the medication twice daily after meals.",
    "Monitor the symptoms for the next 48 hours and update me.",
    "Hi! 👋 Please record a voice note describing your headache:\n• When it started & how often it occurs\n• Time of day it happens\n• What triggers it & what helps it subside\n• Location of pain on your head\n• How it feels\n• Any other symptoms",
  ];

  const patientInitials =
    patient?.name
      ?.split(" ")
      ?.filter(Boolean)
      ?.map((item) => item[0])
      ?.join("")
      ?.slice(0, 2)
      ?.toUpperCase() || "PT";

  const isPaid =
    sub?.subscriptionActive ||
    patient?.subscriptionActive ||
    patient?.paidPerConsultation;

  const chatTimelineItems = useMemo(() => {
    const messageItems = (messages || []).map((item, index) => ({
      type: "message",
      id: getMessageId(item) || `message-${index}`,
      timestamp: item.timestamp,
      order: index,
      message: item,
    }));

    const costingItems = (costingRequests || []).map((request, index) => ({
      type: "costing",
      id: request._id || `costing-${index}`,
      timestamp: request.createdAt,
      order: messages.length + index,
      request,
    }));

    return [...messageItems, ...costingItems].sort((a, b) => {
      const first = new Date(a.timestamp || 0).getTime();
      const second = new Date(b.timestamp || 0).getTime();

      if (first === second) return a.order - b.order;
      return first - second;
    });
  }, [messages, costingRequests]);

  useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, []);

  useEffect(() => {
    if (!doctor || !doctor._id) {
      navigate("/");
    }
  }, [doctor, navigate]);

  useEffect(() => {
    if (!isUserAtBottom.current) return;

    bottomRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  }, [chatTimelineItems]);

  const fetchClient = useCallback(async () => {
    try {
      const res = await axios.get(
        process.env.REACT_APP_BACKEND_URL + `/clients/${client_id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") || null}`,
          },
        }
      );

      if (res.data.success) {
        const nextClient = res.data.client;

        setPatient(nextClient);
        setMessages(nextClient.messages || []);
        updatePatientInCache(client_id, () => nextClient);
      }
    } catch (err) {
      console.error(err);
    }
  }, [client_id]);

  const fetchCostingRequests = useCallback(async () => {
    if (!client_id) return;

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/prescriptions`,
        {
          params: { patientId: client_id },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") || null}`,
          },
        }
      );

      setCostingRequests(res.data?.requests || []);
    } catch (err) {
      console.error("Failed to fetch pharmacy costing request", err);
    }
  }, [client_id]);

  const openPharmacyCall = (pharmacy = {}) => {
    if (!pharmacy.phoneNumber) {
      setMessage("Pharmacy phone number is missing");
      setStatus("warning");
      return;
    }

    window.location.href = `tel:${pharmacy.phoneNumber}`;
  };

  const openPharmacyWhatsapp = (request = {}) => {
    const pharmacy = request.pharmacyId || {};
    const number = getPharmacyContactNumber(pharmacy);

    if (!number) {
      setMessage("Pharmacy WhatsApp number is missing");
      setStatus("warning");
      return;
    }

    const medicines = Array.isArray(request.drugs)
      ? request.drugs
          .map((drug, index) => `${index + 1}. ${drug.name || "Medicine"}`)
          .join("\n")
      : "";

    const body = [
      `Hello ${pharmacy.name || "pharmacy"},`,
      "",
      `Please cost this Zaviramed prescription for ${
        patient?.name || "the patient"
      }.`,
      request._id ? `Request ID: ${request._id}` : "",
      medicines ? `Medicines:\n${medicines}` : "",
      "",
      "Thank you.",
    ]
      .filter(Boolean)
      .join("\n");

    window.open(
      `https://wa.me/${number}?text=${encodeURIComponent(body)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const markPatientMessagesRead = useCallback(async () => {
    if (!client_id) return;

    try {
      await axios.patch(
        process.env.REACT_APP_BACKEND_URL + `/clients/${client_id}/read-all`,
        null,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") || null}`,
          },
        }
      );

      setMessages((prev) =>
        prev.map((item) =>
          item?.sender === "client" ? { ...item, read: true } : item
        )
      );
      setPatient((prev) => {
        if (!prev) return prev;

        const nextPatient = {
          ...prev,
          messages: Array.isArray(prev.messages)
            ? prev.messages.map((item) =>
                item?.sender === "client" ? { ...item, read: true } : item
              )
            : [],
        };

        updatePatientInCache(client_id, () => nextPatient);
        return nextPatient;
      });
      notifyDoctorUnreadMessagesChanged();
    } catch (err) {
      console.error("Failed to mark patient messages as read", err);
    }
  }, [client_id]);

  useEffect(() => {
    fetchClient();
    fetchCostingRequests();

    socket.on("connect", () => {});

    socket.on("connect_error", (err) => {
      console.log("Connection failed:", err.message);
    });

    socket.emit("join_room", client_id);

    const handleNewMessage = (msg) => {
      const doctor = JSON.parse(localStorage.getItem("doctor") || "{}");

      if (msg.doctor_id === doctor._id) {
        if (Notification.permission === "granted") {
          new Notification("New Message from " + msg.sender_name, {
            body: msg.text || "New message received",
            icon: "/icon-192.png",
          });
        }
      }

      if (msg.sender_id === client_id && msg.doctor_id === doctor._id) {
        const nextMessage = {
          ...msg,
          read: msg.sender === "client" ? true : msg.read,
        };

        setMessages((prev) => {
          const nextMessageId = getMessageId(nextMessage);
          const alreadyExists = prev.some((item) => {
            const itemId = getMessageId(item);

            return (
              (nextMessageId && itemId === nextMessageId) ||
              (item?.twilioSid &&
                nextMessage?.twilioSid &&
                item.twilioSid === nextMessage.twilioSid)
            );
          });

          return alreadyExists ? prev : [...prev, nextMessage];
        });
        setPatient((prev) => {
          if (!prev) return prev;

          const existingMessages = Array.isArray(prev.messages)
            ? prev.messages
            : [];
          const nextMessageId = getMessageId(nextMessage);
          const alreadyExists = existingMessages.some((item) => {
            const itemId = getMessageId(item);

            return (
              (nextMessageId && itemId === nextMessageId) ||
              (item?.twilioSid &&
                nextMessage?.twilioSid &&
                item.twilioSid === nextMessage.twilioSid)
            );
          });

          const nextPatient = {
            ...prev,
            messages: alreadyExists
              ? existingMessages
              : [...existingMessages, nextMessage],
          };

          updatePatientInCache(client_id, () => nextPatient);
          return nextPatient;
        });
        markPatientMessagesRead();
      }
    };

    socket.on("new_message", handleNewMessage);

    return () => {
      socket.off("new_message", handleNewMessage);
    };
  }, [client_id, fetchClient, fetchCostingRequests, markPatientMessagesRead]);

  useEffect(() => {
    const handleFocus = () => {
      fetchClient();
      fetchCostingRequests();
    };

    window.addEventListener("focus", handleFocus);

    return () => window.removeEventListener("focus", handleFocus);
  }, [fetchClient, fetchCostingRequests]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchClient();
      fetchCostingRequests();
    }, 10000);

    return () => clearInterval(interval);
  }, [fetchClient, fetchCostingRequests]);

  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    setSendMessage(true);
    const activeReply = replyTarget;

    const msg = {
      clientId: client_id,
      sender: "doctor",
      message: newMessage,
      ...(activeReply
        ? {
            replyId: activeReply.replyId,
            reply: activeReply,
          }
        : {}),
    };

    try {
      const res = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "/send-message",
        msg,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") || null}`,
          },
        }
      );
      const savedMessage =
        res.data?.message && typeof res.data.message === "object"
          ? res.data.message
          : null;

      setNewMessage("");
      setReplyTarget(null);

      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }

      setMessages((prev) => [
        ...prev,
        savedMessage || {
          sender: "doctor",
          text: msg.message,
          timestamp: new Date(),
          read: true,
          ...(activeReply
            ? {
                replyId: activeReply.replyId,
                reply: activeReply,
              }
            : {}),
        },
      ]);

      if (res.data?.success === false) {
        setMessage(res.data?.message || "Message could not be sent directly");
        setStatus("warning");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSendMessage(false);
    }
  };

  const handleReplyToMessage = (targetMessage) => {
    const replyId = getMessageId(targetMessage);

    if (!replyId) {
      setMessage("This message cannot be replied to yet");
      setStatus("warning");
      return;
    }

    setReplyTarget({
      replyId,
      sender: targetMessage.sender || "",
      text: cleanReplyPreview(targetMessage),
      mediaType: targetMessage?.media?.[0]?.type || "",
      timestamp: targetMessage.timestamp,
    });

    setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);
  };

  const handleOpenReply = (replyId) => {
    const cleanReplyId = String(replyId || "");
    const target = messageRefs.current[cleanReplyId];

    if (!target) {
      setMessage("Original message is not visible");
      setStatus("info");
      return;
    }

    target.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightedMessageId(cleanReplyId);

    setTimeout(() => {
      setHighlightedMessageId((current) =>
        current === cleanReplyId ? "" : current
      );
    }, 1800);
  };

  const sendDoctorPaymentLink = async (paymentType = "daily") => {
    if (!patient?._id || sendingPaymentLink) return;

    const isMonthly = paymentType === "monthly";

    setSendingPaymentLink(true);
    setSendingPaymentLinkType(paymentType);

    const headers = {
      Authorization: `Bearer ${localStorage.getItem("token") || null}`,
    };

    try {
      const linkRes = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/doctor/clients/${client_id}/${
          isMonthly ? "monthly-payment-link" : "consultation-payment-link"
        }`,
        {},
        { headers }
      );

      const paymentMessage = linkRes.data?.message;

      if (!linkRes.data?.success || !paymentMessage) {
        throw new Error("Unable to create payment link");
      }

      const sendRes = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "/send-message",
        {
          clientId: client_id,
          sender: "doctor",
          message: paymentMessage,
        },
        { headers }
      );

      if (sendRes.data?.success) {
        setMessages((prev) => [
          ...prev,
          {
            sender: "doctor",
            text: paymentMessage,
            timestamp: new Date(),
            read: true,
          },
        ]);

        setMessage(
          isMonthly ? "Monthly payment link sent" : "Daily payment link sent"
        );
        setStatus("success");
        setPaymentLinkModalOpen(false);
      } else {
        setNewMessage(paymentMessage);
        setMessage(
          sendRes.data?.message ||
            "Payment link created, but it could not be sent automatically."
        );
        setStatus("warning");

        setTimeout(() => {
          textareaRef.current?.focus();
        }, 0);
        setPaymentLinkModalOpen(false);
      }
    } catch (err) {
      console.error(err);
      setMessage(
        err.response?.data?.message ||
          err.message ||
          "Failed to create payment link"
      );
      setStatus("error");
    } finally {
      setSendingPaymentLink(false);
      setSendingPaymentLinkType("");
    }
  };

  const sendDoctorCustomPaymentLink = async ({ amount, description }) => {
    if (!patient?._id || sendingPaymentLink) return null;

    setSendingPaymentLink(true);
    setSendingPaymentLinkType("custom");

    const headers = {
      Authorization: `Bearer ${localStorage.getItem("token") || null}`,
    };

    try {
      const linkRes = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/doctor/clients/${client_id}/custom-payment-link`,
        { amount, description },
        { headers }
      );

      const paymentMessage = linkRes.data?.message;

      if (!linkRes.data?.success || !paymentMessage) {
        throw new Error("Unable to create payment link");
      }

      const sendRes = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "/send-message",
        {
          clientId: client_id,
          sender: "doctor",
          message: paymentMessage,
        },
        { headers }
      );

      if (sendRes.data?.success) {
        setMessages((prev) => [
          ...prev,
          {
            sender: "doctor",
            text: paymentMessage,
            timestamp: new Date(),
            read: true,
          },
        ]);

        setMessage("Payment link sent");
        setStatus("success");

        return { success: true, message: "Payment link sent" };
      }

      setNewMessage(paymentMessage);
      setMessage(
        sendRes.data?.message ||
          "Payment link created, but it could not be sent automatically."
      );
      setStatus("warning");

      setTimeout(() => {
        textareaRef.current?.focus();
      }, 0);

      return {
        success: false,
        warning: true,
        message:
          sendRes.data?.message ||
          "Payment link created, but it could not be sent automatically.",
      };
    } catch (err) {
      console.error(err);
      const errorMessage =
        err.response?.data?.message || err.message || "Failed to create payment link";

      setMessage(errorMessage);
      setStatus("error");
      throw new Error(errorMessage);
    } finally {
      setSendingPaymentLink(false);
      setSendingPaymentLinkType("");
    }
  };

  const handleEmoji = (emoji) => {
    setNewMessage((prev) => prev + emoji.emoji);
  };

  const checkSubscription = async () => {
    try {
      const res = await axios.post(
        process.env.REACT_APP_BACKEND_URL + `/api/paystack/check-subscription`,
        {
          customerCode: patient?.paystackCustomerId,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") || null}`,
          },
        }
      );

      setSub({
        subscriptionActive: res.data.active,
        subscription: res.data.subscription,
      });
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (!client_id) return;

    markPatientMessagesRead();
  }, [client_id, markPatientMessagesRead]);

  useEffect(() => {
    if (patient?.paystackCustomerId) {
      checkSubscription();
    }
  }, [patient]);

  if (!patient) {
    return (
      <div className="flex h-[100dvh] flex-col overflow-hidden bg-slate-50">
        <DoctorHeader />

        <SimpleSnackbar message={message} color={status} />

        <div className="flex min-h-0 flex-1 overflow-hidden">
          <div className="hidden lg:flex">
            <DoctorSidebar activeTab="Patients" setActiveTab={() => {}} />
          </div>

          <DoctorSidebarDrawer
            open={drawerOpen}
            onOpen={() => setDrawerOpen(true)}
            onClose={() => setDrawerOpen(false)}
            activeTab="Patients"
            setActiveTab={() => setDrawerOpen(false)}
          />

          <div className="flex min-h-0 flex-1 items-center justify-center bg-white">
            <ZavLoader />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-[100dvh] flex-col overflow-hidden bg-slate-50">
      <DoctorHeader />

      <SimpleSnackbar message={message} color={status} />

      <PrescriptionsDialog
        openPrescriptions={openPrescriptions}
        setOpenPrescriptions={setOpenPrescriptions}
        patient={patient}
      />

      <SpecialistsDialog
        open={openSpecialistConsultation}
        setOpen={setOpenSpecialistConsultation}
        patient={patient}
      />

      <ReferralDialog
        referralOpen={referralOpen}
        setReferralOpen={setReferralOpen}
        patient={patient}
        hospitals={hospitals}
        setSelectedHospital={setSelectedHospital}
        selectedHospital={selectedHospital}
      />

      {paymentLinkModalOpen && (
        <div className="fixed inset-0 z-[90] flex items-end bg-slate-950/40 px-3 py-4 sm:items-center sm:justify-center sm:p-6">
          <div className="w-full max-w-md rounded-2xl bg-white p-4 shadow-2xl sm:p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-base font-semibold text-slate-950">
                  Send payment link
                </h2>
                <p className="mt-1 text-sm leading-6 text-slate-600">
                  Choose the payment you want this patient to receive in chat.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setPaymentLinkModalOpen(false)}
                disabled={sendingPaymentLink}
                aria-label="Close payment link options"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <X size={18} />
              </button>
            </div>

            <div className="mt-5 grid gap-3">
              <button
                type="button"
                onClick={() => sendDoctorPaymentLink("daily")}
                disabled={sendingPaymentLink}
                className="flex min-h-[76px] items-center justify-between gap-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-left transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <span>
                  <span className="block text-sm font-semibold text-emerald-900">
                    Send daily payment link
                  </span>
                  <span className="mt-1 block text-sm text-emerald-700">
                    ₦2,500 one-time consultation payment
                  </span>
                </span>

                {sendingPaymentLinkType === "daily" ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <CreditCard size={18} />
                )}
              </button>

              <button
                type="button"
                onClick={() => sendDoctorPaymentLink("monthly")}
                disabled={sendingPaymentLink}
                className="flex min-h-[76px] items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <span>
                  <span className="block text-sm font-semibold text-slate-950">
                    Send monthly payment link
                  </span>
                  <span className="mt-1 block text-sm text-slate-600">
                    Monthly Zaviramed subscription access
                  </span>
                </span>

                {sendingPaymentLinkType === "monthly" ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <CreditCard size={18} />
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <div className="hidden lg:flex">
          <DoctorSidebar activeTab={activeTab} setActiveTab={setActiveTab} />
        </div>

        <DoctorSidebarDrawer
          open={drawerOpen}
          onOpen={() => setDrawerOpen(true)}
          onClose={() => setDrawerOpen(false)}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />

        <main className="flex min-h-0 min-w-0 flex-1 overflow-hidden">
          <div className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-white">
            {/* Patient Header */}
            <header className="relative z-30 flex min-h-[72px] shrink-0 items-center justify-between border-b border-slate-200 bg-white px-3 sm:px-5">
              <div className="flex min-w-0 items-center gap-3">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                >
                  <ArrowLeft size={18} />
                </button>

                <button
                  type="button"
                  onClick={() => setDrawerOpen(true)}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 lg:hidden"
                >
                  <Menu size={18} />
                </button>

                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-900 text-sm font-semibold text-white">
                  {patientInitials}
                </div>

                <div className="min-w-0">
                  <h1 className="truncate text-sm font-semibold text-slate-950 sm:text-base">
                    {patient.name || "Unnamed Patient"}
                  </h1>

                  <p className="mt-0.5 truncate text-xs text-slate-500">
                    {patient?.age || "--"} yrs · {patient?.sex || "--"} · Online
                    consultation
                  </p>
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-2">
                <span
                  className={`hidden rounded-full px-3 py-1 text-xs font-medium sm:inline-flex ${
                    isPaid
                      ? "bg-emerald-50 text-emerald-700"
                      : "bg-amber-50 text-amber-700"
                  }`}
                >
                  {isPaid ? "Paid" : "Not paid"}
                </span>

                {!isPaid && (
                  <>
                    <button
                      type="button"
                      onClick={() => setPaymentLinkModalOpen(true)}
                      disabled={sendingPaymentLink}
                      className="hidden h-10 items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60 sm:inline-flex"
                    >
                      {sendingPaymentLink ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <CreditCard size={16} />
                      )}
                      Send payment link
                    </button>

                    <button
                      type="button"
                      onClick={() => setPaymentLinkModalOpen(true)}
                      disabled={sendingPaymentLink}
                      aria-label="Send payment link"
                      title="Send payment link"
                      className="flex h-10 w-10 items-center justify-center rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60 sm:hidden"
                    >
                      {sendingPaymentLink ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <CreditCard size={18} />
                      )}
                    </button>
                  </>
                )}

                <StartCallButton
                  clientId={patient._id}
                  doctorId={doctor._id}
                  patient={patient}
                />

                <button
                  type="button"
                  onClick={() => setInfoDrawerOpen(true)}
                  className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 lg:hidden"
                >
                  <Info size={18} />
                </button>
              </div>
            </header>

            {/* Messages */}
            <section
              className="relative min-h-0 flex-1 overflow-y-auto px-3 pt-5 sm:px-5"
              onScroll={(e) => {
                const el = e.target;
                const threshold = 80;
                const position =
                  el.scrollHeight - el.scrollTop - el.clientHeight;

                isUserAtBottom.current = position < threshold;
                setShowScrollButton(!isUserAtBottom.current);
              }}
              style={{
                WebkitOverflowScrolling: "touch",
                backgroundColor: "#f8fafc",
                paddingBottom: "16px",
              }}
            >
              <div className="mx-auto max-w-4xl space-y-2">
                {chatTimelineItems.map((item, i) => {
                  if (item.type === "costing") {
                    return (
                      <PharmacyCostingChatCard
                        key={item.id}
                        request={item.request}
                        onCall={openPharmacyCall}
                        onWhatsApp={openPharmacyWhatsapp}
                      />
                    );
                  }

                  const m = item.message;
                  const prev =
                    chatTimelineItems[i - 1]?.type === "message"
                      ? chatTimelineItems[i - 1].message
                      : null;
                  const next =
                    chatTimelineItems[i + 1]?.type === "message"
                      ? chatTimelineItems[i + 1].message
                      : null;
                  const messageId = getMessageId(m);
                  const messageAliases = Array.from(
                    new Set(
                      [
                        messageId,
                        m?.twilioSid ? String(m.twilioSid) : "",
                        m?.timestamp ? String(m.timestamp) : "",
                      ].filter(Boolean)
                    )
                  );

                  const isSameSenderAsPrev = prev && prev.sender === m.sender;
                  const isSameSenderAsNext = next && next.sender === m.sender;

                  const isFirstInGroup = !isSameSenderAsPrev;
                  const isLastInGroup = !isSameSenderAsNext;

                  return (
                    <div
                      key={messageId || i}
                      ref={(node) => {
                        if (!messageAliases.length) return;

                        if (node) {
                          messageAliases.forEach((alias) => {
                            messageRefs.current[alias] = node;
                          });
                        } else {
                          messageAliases.forEach((alias) => {
                            delete messageRefs.current[alias];
                          });
                        }
                      }}
                    >
                      <ChatBubble
                        message={m}
                        isFirstInGroup={isFirstInGroup}
                        isLastInGroup={isLastInGroup}
                        isSameSenderAsPrev={isSameSenderAsPrev}
                        isSameSenderAsNext={isSameSenderAsNext}
                        onReply={handleReplyToMessage}
                        onOpenReply={handleOpenReply}
                        highlighted={messageAliases.includes(highlightedMessageId)}
                      />
                    </div>
                  );
                })}

                <div ref={bottomRef} />
              </div>

           </section>

            {/* Composer */}
            <footer
              className="shrink-0 border-t border-slate-200 bg-white px-3 py-2 sm:px-5"
              style={{
                paddingBottom: "max(6px, env(safe-area-inset-bottom))",
              }}
            >
                    {showScrollButton && (
  <button
    type="button"
    onClick={() =>
      bottomRef.current?.scrollIntoView({ behavior: "smooth" })
    }
    className="absolute bottom-20 left-1/2 -translate-x-1/2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-medium text-slate-700 shadow-sm"
  >
    New messages
  </button>
)}
       
              <div className="mx-auto max-w-4xl">
                {replyTarget && (
                  <div className="mb-2 flex items-start justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <div className="flex min-w-0 gap-3">
                      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-[#075E54]">
                        <Reply size={16} />
                      </div>

                      <div className="min-w-0 border-l-4 border-[#075E54] pl-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Replying to{" "}
                          {replyLabel(replyTarget.sender, patient?.name)}
                        </p>

                        <p className="mt-1 line-clamp-2 text-sm leading-5 text-slate-700">
                          {replyTarget.text}
                        </p>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => setReplyTarget(null)}
                      aria-label="Cancel reply"
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-400 hover:bg-white hover:text-slate-700"
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}

                <div className="flex items-end gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2">
                  <div className="flex items-center gap-1 pb-1">
                    <IconButton
                      size="small"
                      onClick={(e) => setTemplateAnchor(e.currentTarget)}
                      className="!text-slate-500"
                    >
                      <FileText size={18} />
                    </IconButton>

                    <div className={hideAudioInput ? "hidden" : "inline"}>
                      <IconButton
                        size="small"
                        onClick={(e) => setEmojiAnchor(e.currentTarget)}
                        className="!text-slate-500"
                      >
                        <Smile size={18} />
                      </IconButton>
                    </div>
                  </div>

                  <textarea
                    ref={textareaRef}
                    rows={1}
                    placeholder="Type a message..."
                    value={newMessage}
                    onFocus={() => setHideAudioInput(true)}
                    onBlur={() => setHideAudioInput(false)}
                    onChange={(e) => {
                      setNewMessage(e.target.value);
                      e.target.style.height = "auto";
                      e.target.style.height =
                        Math.min(e.target.scrollHeight, 120) + "px";
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    style={{ fontSize: "16px" }}
                    className="max-h-[120px] min-h-[36px] flex-1 resize-none overflow-y-auto bg-transparent px-1 py-2 text-sm leading-6 text-slate-800 outline-none placeholder:text-slate-400"
                  />

                  <button
                    type="button"
                    onClick={sendMessage}
                    disabled={!newMessage.trim() || sendingMessage}
                    className={`mb-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition ${
                      newMessage.trim()
                        ? "bg-[#075E54] text-white hover:bg-[#064b43]"
                        : "bg-slate-100 text-slate-400"
                    }`}
                  >
                    {sendingMessage ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <Send size={18} />
                    )}
                  </button>
                </div>
              </div>
            </footer>
          </div>

          {/* Desktop Info Panel */}
          <aside className="hidden min-h-0 w-80 shrink-0 border-l border-slate-200 bg-white lg:flex">
            <PatientInfoPanel
              setInfoDrawerOpen={setInfoDrawerOpen}
              patient={patient}
              setReferralOpen={setReferralOpen}
              setOpenPrescriptions={setOpenPrescriptions}
              setOpenSpecialistConsultation={setOpenSpecialistConsultation}
              openSpecialistConsultation={openSpecialistConsultation}
              isPaid={isPaid}
              onSendCustomPaymentLink={sendDoctorCustomPaymentLink}
              customPaymentSending={
                sendingPaymentLink && sendingPaymentLinkType === "custom"
              }
            />
          </aside>

          {/* Mobile Info Drawer */}
          <SwipeableDrawer
            anchor="right"
            open={infoDrawerOpen}
            onOpen={() => setInfoDrawerOpen(true)}
            onClose={() => setInfoDrawerOpen(false)}
            disableSwipeToOpen={false}
            swipeAreaWidth={36}
            ModalProps={{ keepMounted: true }}
            PaperProps={{
              sx: {
                width: { xs: "100%", sm: "100%" },
              },
            }}
          >
            <PatientInfoPanel
              setInfoDrawerOpen={setInfoDrawerOpen}
              patient={patient}
              setReferralOpen={setReferralOpen}
              setOpenPrescriptions={setOpenPrescriptions}
              setOpenSpecialistConsultation={setOpenSpecialistConsultation}
              openSpecialistConsultation={openSpecialistConsultation}
              isPaid={isPaid}
              onSendCustomPaymentLink={sendDoctorCustomPaymentLink}
              customPaymentSending={
                sendingPaymentLink && sendingPaymentLinkType === "custom"
              }
            />
          </SwipeableDrawer>
        </main>
      </div>

      {/* Emoji Picker */}
      <Popover
        open={Boolean(emojiAnchor)}
        anchorEl={emojiAnchor}
        onClose={() => setEmojiAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <EmojiPicker onEmojiClick={handleEmoji} />
      </Popover>

      {/* Template Picker */}
      <Popover
        open={Boolean(templateAnchor)}
        anchorEl={templateAnchor}
        onClose={() => setTemplateAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <div className="w-72 p-3">
          <p className="mb-2 px-2 text-xs font-semibold text-slate-400">
            Quick replies
          </p>

          <div className="space-y-1">
            {quickTemplates.map((t, i) => (
              <button
                key={i}
                type="button"
                onClick={() => {
                  setNewMessage(t);
                  setTemplateAnchor(null);
                }}
                className="w-full rounded-xl px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
              >
                {t.split("\n")[0]}...
              </button>
            ))}
          </div>
        </div>
      </Popover>
    </div>
  );
};

export default DoctorChatPage;
